'use client';

import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Locale, SUPPORTED_LOCALES, formatMessage, messages } from './messages';

interface I18nContextValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: string, values?: Record<string, string | number>) => string;
  isRtl: boolean;
}

const I18nContext = createContext<I18nContextValue | null>(null);

const STORAGE_KEY = 'imail.locale';

const detect = (): Locale => {
  if (typeof window === 'undefined') return 'en';
  const stored = window.localStorage.getItem(STORAGE_KEY) as Locale | null;
  if (stored && SUPPORTED_LOCALES.some((l) => l.code === stored)) return stored;
  const nav = (navigator.language || 'en').slice(0, 2).toLowerCase();
  return (SUPPORTED_LOCALES.some((l) => l.code === nav) ? nav : 'en') as Locale;
};

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>('en');

  useEffect(() => {
    const initial = detect();
    setLocaleState(initial);
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const cfg = SUPPORTED_LOCALES.find((l) => l.code === locale);
    document.documentElement.lang = locale;
    document.documentElement.dir = cfg?.rtl ? 'rtl' : 'ltr';
  }, [locale]);

  const setLocale = (l: Locale) => {
    setLocaleState(l);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, l);
    }
  };

  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      setLocale,
      isRtl: !!SUPPORTED_LOCALES.find((l) => l.code === locale)?.rtl,
      t: (key, values) => {
        // Fall back to English, then to the key itself, so a missing
        // translation never renders as empty.
        const dict = messages[locale] || messages.en;
        const tpl = dict[key] || messages.en[key] || key;
        return formatMessage(tpl, values);
      },
    }),
    [locale]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useTranslation() {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    // Fallback so components imported in pages that haven't wrapped
    // I18nProvider yet still render the English string.
    return {
      locale: 'en' as Locale,
      setLocale: () => {},
      isRtl: false,
      t: (key: string, values?: Record<string, string | number>) => {
        const tpl = messages.en[key] || key;
        return formatMessage(tpl, values);
      },
    };
  }
  return ctx;
}
